import { create } from 'zustand';
import html2canvas from 'html2canvas';
import { getRecentLogs } from '@/services/consoleCapture';
import { uploadBugScreenshot, submitBugReport } from '@/services/data/bugReports';

interface BugReportState {
  isSubmitting: boolean;
  lastError: string | null;
  preCapturedScreenshot: string | null;
  captureScreenshot: () => Promise<void>;
  clearScreenshot: () => void;
  submitReport: (params: {
    reporterId: string;
    description: string;
    priority: 'blocking' | 'important' | 'minor';
    category: 'ui' | 'data' | 'performance' | 'navigation' | 'other';
    currentView: string | null;
    userRoles: string[];
    isImpersonating: boolean;
    impersonatedUserId: string | null;
    annotatedScreenshotDataUrl?: string | null;
  }) => Promise<string | null>; // Returns bug report ID or null on failure
}

function parseBrowserInfo() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';

  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/')) browser = 'Safari';

  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return {
    user_agent: ua,
    screen_size: `${globalThis.innerWidth}x${globalThis.innerHeight}`,
    os,
    browser,
  };
}

export const useBugReportStore = create<BugReportState>((set, get) => ({
  isSubmitting: false,
  lastError: null,
  preCapturedScreenshot: null,

  captureScreenshot: async () => {
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
        logging: false,
      });
      set({ preCapturedScreenshot: canvas.toDataURL('image/png', 0.9) });
    } catch (e) {
      console.warn('Pre-capture screenshot failed:', e);
    }
  },

  clearScreenshot: () => set({ preCapturedScreenshot: null }),

  submitReport: async (params) => {
    set({ isSubmitting: true, lastError: null });

    try {
      // Upload original screenshot
      let screenshotUrl: string | null = null;
      try {
        const canvas = await html2canvas(document.body, {
          useCORS: true,
          allowTaint: true,
          scale: 0.5,
          logging: false,
        });
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, 'image/png', 0.7)
        );
        if (blob) {
          const result = await uploadBugScreenshot(params.reporterId, blob);
          if (result.success && result.url) {
            screenshotUrl = result.url;
          }
        }
      } catch (e) {
        console.warn('Screenshot capture failed:', e);
      }

      // Upload annotated screenshot if provided
      let annotatedScreenshotUrl: string | null = null;
      if (params.annotatedScreenshotDataUrl) {
        try {
          const resp = await fetch(params.annotatedScreenshotDataUrl);
          const blob = await resp.blob();
          const uploadResult = await uploadBugScreenshot(params.reporterId, blob);
          if (uploadResult.success && uploadResult.url) {
            annotatedScreenshotUrl = uploadResult.url;
          }
        } catch (e) {
          console.warn('Annotated screenshot upload failed:', e);
        }
      }

      const result = await submitBugReport({
        reporter_id: params.reporterId,
        description: params.description,
        priority: params.priority,
        category: params.category,
        page_url: globalThis.location.href,
        current_view: params.currentView,
        user_roles: params.userRoles,
        is_impersonating: params.isImpersonating,
        impersonated_user_id: params.impersonatedUserId,
        console_logs: getRecentLogs(),
        browser_info: parseBrowserInfo(),
        screenshot_url: annotatedScreenshotUrl || screenshotUrl,
      });

      if (!result.success) {
        set({ isSubmitting: false, lastError: 'Erreur lors de l\'envoi du rapport.' });
        return null;
      }

      set({ isSubmitting: false });
      return result.id || null;
    } catch (error) {
      console.error('Error submitting bug report:', error);
      set({ isSubmitting: false, lastError: 'Erreur inattendue.' });
      return null;
    }
  },
}));
